import { Module, MutationTree, ActionTree } from 'vuex';
import { RootStateInterface } from '@/store/rootState';
import { AnalyticsApi } from '../api/analytics';
import {
  AnalyticsFilters,
  ResponseRate,
  CompletionTime,
  QuestionPerformance,
  UserSegmentation,
  Trend,
  SentimentAnalysis,
} from '../interfaces/analytics';

export interface AnalyticsStateInterface {
  filters: AnalyticsFilters;
  responseRates: ResponseRate[];
  completionTimes: CompletionTime[];
  questionPerformance: QuestionPerformance[];
  userSegmentation: UserSegmentation[];
  trends: Trend[];
  sentimentAnalysis: SentimentAnalysis | null;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

export const state: AnalyticsStateInterface = {
  filters: {
    period: 'daily',
  },
  responseRates: [],
  completionTimes: [],
  questionPerformance: [],
  userSegmentation: [],
  trends: [],
  sentimentAnalysis: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

export const mutations: MutationTree<AnalyticsStateInterface> = {
  SET_FILTERS(st: AnalyticsStateInterface, payload: AnalyticsFilters) {
    st.filters = { ...st.filters, ...payload };
  },
  SET_RESPONSE_RATES(st: AnalyticsStateInterface, payload: ResponseRate[]) {
    st.responseRates = payload;
  },
  SET_COMPLETION_TIMES(st: AnalyticsStateInterface, payload: CompletionTime[]) {
    st.completionTimes = payload;
  },
  SET_QUESTION_PERFORMANCE(st: AnalyticsStateInterface, payload: QuestionPerformance[]) {
    st.questionPerformance = payload;
  },
  SET_USER_SEGMENTATION(st: AnalyticsStateInterface, payload: UserSegmentation[]) {
    st.userSegmentation = payload;
  },
  SET_TRENDS(st: AnalyticsStateInterface, payload: Trend[]) {
    st.trends = payload;
  },
  SET_SENTIMENT_ANALYSIS(st: AnalyticsStateInterface, payload: SentimentAnalysis | null) {
    st.sentimentAnalysis = payload;
  },
  SET_LOADING(st: AnalyticsStateInterface, payload: boolean) {
    st.loading = payload;
  },
  SET_ERROR(st: AnalyticsStateInterface, payload: string | null) {
    st.error = payload;
  },
  SET_LAST_UPDATED(st: AnalyticsStateInterface, payload: string) {
    st.lastUpdated = payload;
  },
  CLEAR_ANALYTICS(st: AnalyticsStateInterface) {
    st.responseRates = [];
    st.completionTimes = [];
    st.questionPerformance = [];
    st.userSegmentation = [];
    st.trends = [];
    st.sentimentAnalysis = null;
    st.error = null;
    st.lastUpdated = null;
  },
};

export const actions: ActionTree<AnalyticsStateInterface, RootStateInterface> = {
  async fetchAnalytics({ commit, state }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const api = new AnalyticsApi();

      const [rates, times, performance, segmentation, trendData, sentiment] = await Promise.all([
        api.getResponseRates(state.filters),
        api.getCompletionTimes(state.filters),
        api.getQuestionPerformance(state.filters),
        api.getUserSegmentation(state.filters),
        api.getTrends(state.filters),
        api.getSentimentAnalysis(state.filters),
      ]);

      if (rates.success && rates.data) commit('SET_RESPONSE_RATES', rates.data);
      if (times.success && times.data) commit('SET_COMPLETION_TIMES', times.data);
      if (performance.success && performance.data)
        commit('SET_QUESTION_PERFORMANCE', performance.data);
      if (segmentation.success && segmentation.data)
        commit('SET_USER_SEGMENTATION', segmentation.data);
      if (trendData.success && trendData.data) commit('SET_TRENDS', trendData.data);
      if (sentiment.success && sentiment.data) commit('SET_SENTIMENT_ANALYSIS', sentiment.data);

      commit('SET_LAST_UPDATED', new Date().toISOString());
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to load analytics';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  updateFilters({ commit }, filters: AnalyticsFilters) {
    commit('SET_FILTERS', filters);
  },
};

const namespaced: boolean = true;

export const analytics: Module<AnalyticsStateInterface, RootStateInterface> = {
  namespaced,
  state,
  mutations,
  actions,
};
